import React from 'react';
import PropTypes from 'prop-types';
import {Translate} from 'react-redux-i18n';
import {useDispatch} from 'react-redux';

import {documentUtils} from 'front-onceforall-core';
import {useStateHook} from 'front-onceforall-core/dist/utils/customHooks';

import {captureError} from '../../utils/log';
import {addError, addSuccess} from '../../actions/snackbars';
import documentService from '../../services/document/DocumentService';
import signatureService from '../../services/signature/SignatureService';

import ModalWaitingSignature from '../document/ModalWaitingSignature';
import UploadPanel from '../document/UploadPanel';
import EditAdditionalDesignationModal from '../document/EditAdditionalDesignationModal';

import * as utils from './utils';
import DossierList from './DossierList';

function DocumentListEdit(props) {
  const dispatch = useDispatch();
  const [state, setState] = useStateHook({
    document: {},
    modalAdditionalDesignationIsOpen: false,
    submitting: false,
    correlationIdOpened: null,
    showWaitingSignature: false,
    templateFile: '',
    documentPermissions: {}
  });

  let triggerUpload = () => { /* placeholder */ };

  function handleCloseAdditionalDesignationModal() {
    setState({modalAdditionalDesignationIsOpen: false});
  }

  function handleEditAdditionalDesignation(document) {
    setState({modalAdditionalDesignationIsOpen: true, document});
  }

  React.useEffect(() => {
    const documentPermissions = utils.getDocumentPermissions(props.depositor);
    setState({documentPermissions});
  }, [props.depositor]);

  function handleDocumentSign(document) {
    setState({showWaitingSignature: true});
    signatureService.signDocument(document.uid)
      .then(response => window.open(response.url, '_self'))
      .catch(error => {
        captureError('handleDocumentSign', error);
        dispatch(addError(<Translate value="error.generic"/>));
        setState({showWaitingSignature: false});
      });
  }

  function handleDuplicateDocument(document) {
    documentService.duplicateDocument(document.uid)
      .then(props.onRefreshDocuments)
      .catch(() => {
        dispatch(addError(<Translate value="error.generic"/>));
      });
  }

  function handleDocumentDelete(document) {
    documentService.deleteDocument(props.organisationId, document)
      .then(() => {
        props.onRefreshDocuments();
        dispatch(addSuccess(<Translate value="message.changesSaved"/>));
      })
      .catch(() => {
        dispatch(addError(<Translate value="error.generic"/>));
      });
  }

  function handleSubmitAdditionalDesignation(additionalDesignation) {
    if (state.submitting) {
      return;
    }

    setState({submitting: true});

    documentService.updateAdditionalDesignation(state.document.uid, additionalDesignation)
      .then(() => {
        setState({
          document: {
            ...state.document,
            additionalDesignation
          }
        });
        dispatch(addSuccess(<Translate value="document.additionalDesignationSuccessUpdate"/>));
        props.onRefreshDocuments();
      })
      .catch(() => {
        dispatch(addError(<Translate value="error.generic"/>));
      })
      .finally(() => {
        setState({submitting: false, modalAdditionalDesignationIsOpen: false});
      });
  }

  const documentActions = documentUtils.constructDocumentActions({
    onEditAdditionalDesignation: handleEditAdditionalDesignation,
    onSign: handleDocumentSign,
    onUpload: document => triggerUpload(document),
    onAddNewVersion: document => triggerUpload(document),
    onDownload: document => utils.handleDocumentFileOpen(document, false),
    onDownloadExcel: document => utils.handleDocumentFileOpen(document, true),
    onDownloadProof: utils.handleDocumentProofFileOpen,
    onDuplicate: handleDuplicateDocument,
    onDelete: handleDocumentDelete,
    onOpen: utils.handleDocumentOpen
  });

  return (
    <>
      <DossierList
        documents={props.documents}
        documentPermissions={state.documentPermissions}
        documentActions={documentActions}
        correlationIdOpened={state.correlationIdOpened}
        organisationId={props.organisationId}
        onClickDocument={correlationIdOpened => setState({correlationIdOpened})}
      />
      <UploadPanel
        organisationId={props.organisationId}
        setUploadTrigger={uploadFunction => {
          triggerUpload = uploadFunction;
        }}
        onUploadSuccess={props.onRefreshDocuments}
        refreshDocuments={props.onRefreshDocuments}
      />
      {state.modalAdditionalDesignationIsOpen &&
        <EditAdditionalDesignationModal
          submitting={state.submitting}
          onClose={handleCloseAdditionalDesignationModal}
          onSubmit={handleSubmitAdditionalDesignation}
          document={state.document}
        />
      }
      {state.showWaitingSignature && <ModalWaitingSignature isOpen={state.showWaitingSignature}/>}
    </>
  );
}

DocumentListEdit.defaultProps = {
  documents: [],
  depositor: false
};

DocumentListEdit.propTypes = {
  organisationId: PropTypes.number.isRequired,
  documents: PropTypes.array,
  onRefreshDocuments: PropTypes.func.isRequired,
  depositor: PropTypes.bool
};

export default DocumentListEdit;
